import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import { PAGINATION_PAGE_SIZE } from 'basics/constants/common.constant';
import {
  fromUnitsToRem, YodaBorderRadius,
  YodaColors,
  YodaFontSize,
  YodaFontWeight,
  YodaJustifyContent,
  YodaSpacing,
} from 'yoda-ui/yodaTheme';

export const gridListStyle: SxProps<Theme> = {
  '& .MuiDataGrid-root': {
    border: 'none',
    maxHeight: '895px',
    paddingY: fromUnitsToRem(YodaSpacing.medium),
  },

  '& .MuiDataGrid-main': {
    minHeight: '300px',
    border: 'none',
    overflowY: 'auto',
  },
};

export const gridListLoadingStyle: SxProps<Theme> = {
  '& .MuiDataGrid-main > div:first-of-type': {
    height: 'calc(100% - 30px) !important',
    top: '30px !important',
  },
};

export const dashboardGridListStyle: SxProps<Theme> = {
  '& .MuiDataGrid-root': {
    border: 'none',
    padding: YodaSpacing.medium,
    maxHeight: '790px',
    overflow: 'auto',
  },

  '& .MuiDataGrid-main': {
    minHeight: '300px',
    border: 'none',
    overflowY: 'auto',
  },
};

export const gridListHeadStyle: SxProps<Theme> = {
  display: 'flex',
  justifyContent: YodaJustifyContent.spaceBetween,
  height: '80px',

  '& .MuiButton-root': {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    paddingY: fromUnitsToRem(YodaSpacing.xSmall),
  },
};

export const gridListTitleStyle: SxProps<Theme> = {
  fontWeight: YodaFontWeight.bold,
  fontSize: YodaFontSize.xLargePlus,
  marginBottom: YodaSpacing.large,
};

export const gridListCardStyle: SxProps<Theme> = {
  padding: '0',
  background: 'transparent',
  boxShadow: 'none',
  borderTop: `1px solid ${YodaColors.neutralGrey5}`,
  borderRadius: 0,
};

export const dashboardGridListCardStyle: SxProps<Theme> = {
  padding: '0',
};

export const gridListFiltersStyle = (theme: Theme): SxProps<Theme> => ({
  marginBottom: fromUnitsToRem(YodaSpacing.medium),
  display: 'block',

  '& > .MuiBox-root': {
    '& > .MuiAutocomplete-root, & > .MuiFormControl-root': {
      background: YodaColors.white,
    },
  },

  [theme.breakpoints.down('md')]: {
    '& > .MuiBox-root': {
      display: 'inline-block',
      width: '300px',
      marginRight: fromUnitsToRem(YodaSpacing.small),
      marginBottom: fromUnitsToRem(YodaSpacing.small),
    },
  },

  [theme.breakpoints.up('md')]: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: fromUnitsToRem(YodaSpacing.small),

    '& > .MuiBox-root': {
      width: `calc((100% - calc(${fromUnitsToRem(YodaSpacing.small)})) / 2)`,
    },
  },

  [theme.breakpoints.up('lg')]: {
    '& > .MuiBox-root': {
      width: `calc((100% - calc(${fromUnitsToRem(YodaSpacing.small)} * 2)) / 3)`,
    },
  },

  [theme.breakpoints.up('xl')]: {
    '& > .MuiBox-root': {
      width: `calc((100% - calc(${fromUnitsToRem(YodaSpacing.small)} * 3)) / 4)`,
    },
  },
});

export const gridListDataStyle = (pageSize: number): SxProps<Theme> => ({
  '& .MuiDataGrid-virtualScroller': {
    overflowX: 'hidden',
    marginTop: `${fromUnitsToRem(YodaSpacing.small)} !important`,
  },

  '& .MuiDataGrid-cell:focus-within': {
    outline: 'none',
  },

  '& .MuiDataGrid-virtualScrollerContent': {
    paddingBottom: YodaSpacing.small,
    paddingTop: fromUnitsToRem(YodaSpacing.xSmall),
    height: 'fit-content !important',
    width: pageSize > PAGINATION_PAGE_SIZE ? 'calc(100% - 20px) !important' : '100%',
  },

  '& .MuiDataGrid-columnHeaders': {
    lineHeight: 'initial !important',
    alignItems: 'flex-start',
    fontSize: YodaFontSize.large,
    borderRadius: '5px 5px 0 0',
    border: 'none',
    minHeight: 'initial !important',
    maxHeight: 'initial !important',

    '& .MuiDataGrid-columnSeparator': {
      display: 'none',
    },

    '& .MuiDataGrid-columnHeaderTitle': {
      fontWeight: YodaFontWeight.medium,
      fontSize: YodaFontSize.small,
      color: YodaColors.textSecondary,
    },
  },

  '& .MuiDataGrid-columnHeader': {
    paddingLeft: '10px',
  },

  '& .MuiDataGrid-columnHeader:first-of-type': {
    paddingLeft: '24px',
  },

  '& .MuiDataGrid-columnHeader:last-of-type, & .MuiDataGrid-cell:last-of-type': {
    '& .MuiDataGrid-columnSeparator': {
      display: 'none',
    },
  },

  '& .MuiDataGrid-columnHeader:focus-within': {
    outline: 'none',
  },

  '& .MuiDataGrid-virtualScrollerRenderZone': {
    position: 'relative',
    '& > *': {
      width: '100%',
    },
  },

  '& .MuiDataGrid-virtualScrollerRenderZone > .MuiDataGrid-row': {
    backgroundColor: `${YodaColors.white}`,
    marginY: '4px !important',
    maxHeight: '56px !important',
    width: '100%',
  },

  '& .MuiDataGrid-row': {
    borderBottom: 'none',
    boxShadow: `inset 0 0 0 1px ${YodaColors.gray2}`,
    borderRadius: YodaBorderRadius.small,
    transition: 'box-shadow .2s ease-out, background-color .2s ease-out',
    fontWeight: YodaFontWeight.light,
    fontSize: YodaFontSize.small,
  },

  '& .MuiDataGrid-row:hover, & .MuiDataGrid-row:focus': {
    backgroundColor: YodaColors.primaryLighter,
    boxShadow: '0 2px 10px 0 rgba(18, 32, 53, 0.1)',
  },

  '& .MuiDataGrid-cell:last-of-type svg': {
    width: '25px',
    height: '25px',
    color: YodaColors.blue7,
  },

  '& .MuiDataGrid-cell[data-field="action"] svg': {
    width: '25px',
    height: '25px',
    color: YodaColors.blue7,
  },

  '& .MuiDataGrid-cell': {
    borderBottom: 'none',
    paddingY: '17px',
    maxHeight: '56px !important',
    fontSize: YodaFontSize.small,
    color: YodaColors.textPrimary,
  },

  '& .gridlist-cell-bold': {
    fontWeight: YodaFontWeight.medium,
    paddingLeft: fromUnitsToRem(YodaSpacing.medium),
    width: '100%',
    minWidth: 'initial !important',

    '&:last-of-type': {
      borderColor: `${YodaColors.gray2} !important`,
    },
  },

  '& .MuiDataGrid-footerContainer': {
    paddingTop: fromUnitsToRem(YodaSpacing.xxSmall),
    border: 'none',
  },

  '& .MuiDataGrid-footerContainer > div:first-of-type': {
    display: 'none',
  },

  '& .MuiDataGrid-footerContainer > .MuiFormControl-root .MuiSelect-select': {
    paddingTop: fromUnitsToRem(YodaSpacing.xxxSmall),
    paddingBottom: fromUnitsToRem(YodaSpacing.xxxSmall),
  },

  '& .MuiDataGrid-cell[style*="e-"]': {
    display: 'none !important',
  },
});

export const dashboardGridListDataStyle: SxProps<Theme> = {
  '& .MuiDataGrid-virtualScroller': {
    marginTop: `${fromUnitsToRem(YodaSpacing.large)} !important`,
  },

  '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeaderTitle': {
    fontWeight: YodaFontWeight.bold,
    fontSize: YodaFontSize.xLarge,
    color: YodaColors.textPrimary,
  },
};

export const dashboardGridListRowStyle: SxProps<Theme> = {
  '& .MuiDataGrid-virtualScrollerRenderZone > .MuiDataGrid-row': {
    marginY: '4px !important',
    maxHeight: '56px !important',
  },

  '& .MuiDataGrid-cell': {
    borderBottom: 'none',
    maxHeight: '56px !important',
  },

  '& .MuiDataGrid-columnHeader:first-of-type': {
    padding: 0,
  },

  '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeaderTitle': {
    fontWeight: YodaFontWeight.bold,
  },
};

export const onCellClickStyle: SxProps<Theme> = {
  '& .MuiDataGrid-row': {
    borderRadius: YodaBorderRadius.small,
  },

  '& .MuiDataGrid-row:hover': {
    cursor: 'pointer',
    backgroundColor: YodaColors.primaryLighter,
  },
};

export const gridListActionStyle: SxProps<Theme> = {
  borderRadius: YodaBorderRadius.small,
};
